import { buyerControlDisplayName, DEMONSTRATION_CONTROL_LABEL } from '../domain/buyerFacing/conditionDisplayNames';
import { pickSharedWorkflowControl } from '../domain/safetyCases/investigateControlLink';
import { usefulControlText } from '../domain/safetyCases/safetyCaseOverviewPresenter';
import {
  formatControlStatusLabel,
  mapControlStatusFromApi,
} from '../domain/safetyCases/safetyCaseControlsPresenter';
import type { WorkflowCorrectiveActionListResponse } from '../services/reportsApi';

export const OVERVIEW_V2_ROOM_B_NEAR_MISS_ID = '6d79065c-c8fa-5193-a7eb-6a922c6cea64';
export const OVERVIEW_V2_ROOM_A_ZONE_INTRUSION_ID = 'cc7abe63-bcb2-512d-83c6-69ea0ffb120c';

export type OverviewV2Binding = 'live' | 'fixture';

export type OverviewV2RecordedControl = {
  controlId: string;
  title: string;
  statusLabel?: string | null;
  recordedAt?: string | null;
};

export type OverviewV2WorkflowIdentity = {
  conditionId: string;
  patternKey?: string;
  caseId?: string;
  ccrId?: string;
  recordedControl?: OverviewV2RecordedControl | null;
};

function text(value: unknown): string {
  return String(value || '').trim();
}

export function buildOverviewV2PatternKey(args: {
  incidentType?: string | null;
  zoneId?: string | null;
  cameraId?: string | null;
  deviceId?: string | null;
}): string | null {
  const incidentType = text(args.incidentType);
  const zoneId = text(args.zoneId);
  const cameraId = text(args.cameraId);
  const deviceId = text(args.deviceId);
  if (!incidentType || !zoneId || !cameraId || !deviceId) return null;
  return `${incidentType}|${zoneId}|${cameraId}|${deviceId}`;
}

export function matchOverviewV2SafetyCaseId(args: {
  cases: Array<{ safety_case_id?: string | null; finding_id?: string | null; workflow_id?: string | null }>;
  patternKey?: string | null;
}): string | null {
  const key = text(args.patternKey);
  if (!key) return null;
  const matches = args.cases
    .map((row) => ({
      id: text(row.safety_case_id || row.finding_id),
      workflow: text(row.workflow_id),
    }))
    .filter((row) => row.id && row.workflow === key);
  if (matches.length !== 1) return null;
  return matches[0].id;
}

export function attachOverviewV2SafetyCaseIds(
  identities: OverviewV2WorkflowIdentity[],
  cases: Array<{ safety_case_id?: string | null; finding_id?: string | null; workflow_id?: string | null }>,
): OverviewV2WorkflowIdentity[] {
  return identities.map((identity) => {
    const caseId = matchOverviewV2SafetyCaseId({ cases, patternKey: identity.patternKey });
    return caseId ? { ...identity, caseId } : identity;
  });
}

export function overviewV2RecordedControlFromActions(
  actions: {
    active_actions?: Array<{
      action_id?: string | null;
      title?: string | null;
      description?: string | null;
      status?: string | null;
      implemented_at?: string | null;
      completed_at?: string | null;
    }> | null;
    completed_actions?: Array<{
      action_id?: string | null;
      title?: string | null;
      description?: string | null;
      status?: string | null;
      implemented_at?: string | null;
      completed_at?: string | null;
    }> | null;
  } | null | undefined,
): OverviewV2RecordedControl | null {
  const picked = pickSharedWorkflowControl(actions);
  const title = usefulControlText(picked?.title) || usefulControlText(picked?.description);
  const controlId = text(picked?.action_id);
  if (!controlId && !title) return null;
  const dates = picked as { implemented_at?: string | null; completed_at?: string | null } | null;
  return {
    controlId: controlId || 'recorded',
    title: title || 'Recorded control',
    statusLabel: formatControlStatusLabel(mapControlStatusFromApi(picked?.status)),
    recordedAt: text(dates?.implemented_at) || text(dates?.completed_at) || null,
  };
}

export async function attachOverviewV2RecordedControls(
  identities: OverviewV2WorkflowIdentity[],
  listActions: (workflowId: string) => Promise<WorkflowCorrectiveActionListResponse>,
): Promise<OverviewV2WorkflowIdentity[]> {
  const keys = Array.from(new Set(
    identities.map((identity) => text(identity.patternKey)).filter(Boolean),
  ));
  if (!keys.length) return identities;
  const byKey = new Map<string, OverviewV2RecordedControl | null>();
  await Promise.all(keys.map(async (key) => {
    try {
      byKey.set(key, overviewV2RecordedControlFromActions(await listActions(key)));
    } catch {
      byKey.set(key, null);
    }
  }));
  return identities.map((identity) => {
    const recorded = identity.patternKey ? byKey.get(identity.patternKey) : null;
    return recorded ? { ...identity, recordedControl: recorded } : identity;
  });
}

export const OVERVIEW_V2_FIXTURE_LEAKAGE_PATTERNS = [
  /Fixture evidence still/i,
  /fixture provider geometry/i,
  /Geometry source:/i,
  /local fixture harness/i,
  /development fixture/i,
];

export function overviewV2HasFixtureLeakage(text: string): boolean {
  return OVERVIEW_V2_FIXTURE_LEAKAGE_PATTERNS.some((pattern) => pattern.test(text));
}

export function resolveOverviewV2ControlName(args: {
  answer?: string | null;
  controlLocation?: string | null;
  controlName?: string | null;
}): string {
  const named = buyerControlDisplayName(args.controlName);
  if (named) return named;
  const answer = String(args.answer || '').toLowerCase();
  if (answer.includes('barrier') || /walkway barrier/i.test(answer)) return DEMONSTRATION_CONTROL_LABEL;
  return 'Recorded control';
}
